import React, { useEffect, useState } from "react";
import { Ticket, Trophy, Clock, Gift, Medal, History, ChevronUp } from "lucide-react";
import { api } from "../lib/api";
import Reveal from "../components/site/Reveal";
import { CrownIcon } from "../components/site/Navbar";
import { useAuth } from "../context/AuthContext";

const pad = (n) => String(n).padStart(2, "0");

const useCountdown = (endsAt) => {
  const [left, setLeft] = useState(null);
  useEffect(() => {
    if (!endsAt) return undefined;
    const tick = () => {
      const diff = new Date(endsAt).getTime() - Date.now();
      if (diff <= 0) {
        setLeft({ ended: true, d: 0, h: 0, m: 0, s: 0 });
        return;
      }
      setLeft({
        ended: false,
        d: Math.floor(diff / 86400000),
        h: Math.floor((diff / 3600000) % 24),
        m: Math.floor((diff / 60000) % 60),
        s: Math.floor((diff / 1000) % 60),
      });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [endsAt]);
  return left;
};

const TOP_CONF = {
  1: {
    label: "1st place",
    order: "order-1 sm:order-2",
    size: "w-full sm:w-[300px] sm:-mt-6",
    ring: "border-[#ffd94a]",
    glow: "shadow-[0_18px_50px_rgba(255,217,74,0.18)]",
    badge: "bg-gradient-to-b from-[#ffe887] to-[#e2b422] text-[#6b4a0e]",
    bar: "bg-gradient-to-r from-[#ffe887] to-[#e2b422]",
    accent: "text-[#ffd94a]",
  },
  2: {
    label: "2nd place",
    order: "order-2 sm:order-1",
    size: "w-full sm:w-[270px]",
    ring: "border-[#cdd9e8]",
    glow: "shadow-[0_18px_44px_rgba(2,20,44,0.35)]",
    badge: "bg-gradient-to-b from-[#f2f7fd] to-[#a6bcd4] text-[#39475a]",
    bar: "bg-gradient-to-r from-[#f2f7fd] to-[#a6bcd4]",
    accent: "text-[#e3edf8]",
  },
  3: {
    label: "3rd place",
    order: "order-3 sm:order-3",
    size: "w-full sm:w-[270px]",
    ring: "border-[#e0985c]",
    glow: "shadow-[0_18px_44px_rgba(2,20,44,0.35)]",
    badge: "bg-gradient-to-b from-[#f6cda4] to-[#b97a3c] text-[#4a2c14]",
    bar: "bg-gradient-to-r from-[#f6cda4] to-[#b97a3c]",
    accent: "text-[#f2b078]",
  },
};

const odds = (tickets, total) => (total > 0 ? Math.round((tickets / total) * 1000) / 10 : 0);

const TopEntryCard = ({ place, entry, total }) => {
  const c = TOP_CONF[place];
  const pct = entry ? odds(entry.tickets, total) : 0;
  return (
    <div
      data-testid={`raffle-top-card-${place}`}
      className={`relative ${c.size} ${c.order} shrink-0 rounded-3xl border border-white/12 bg-[rgba(6,32,66,0.5)] backdrop-blur-md px-6 pt-7 pb-6 flex flex-col items-center text-center ${c.glow}`}
    >
      <span
        className={`absolute -top-3.5 left-1/2 -translate-x-1/2 px-3.5 py-1 rounded-full text-[0.6rem] font-bold tracking-[0.22em] uppercase ${c.badge} border border-black/15 shadow-[0_4px_12px_rgba(0,0,0,0.3)] flex items-center gap-1`}
      >
        {place === 1 ? <Trophy className="w-3 h-3" strokeWidth={2.8} /> : <Medal className="w-3 h-3" strokeWidth={2.8} />}
        {c.label}
      </span>
      {entry ? (
        <>
          <span className={`w-16 h-16 sm:w-[76px] sm:h-[76px] rounded-full border-[3px] ${c.ring} p-0.5 bg-white/5`}>
            <img src={entry.avatar_url} alt="" className="w-full h-full rounded-full object-cover" />
          </span>
          <p className="mt-3 text-white font-display font-bold text-[1.1rem] leading-tight max-w-full truncate">
            {entry.username}
          </p>
          <p className={`mt-1 flex items-center gap-1.5 ${c.accent} text-[0.82rem] font-bold`}>
            <Ticket className="w-4 h-4" strokeWidth={2.6} />
            {entry.tickets.toLocaleString()} tickets
          </p>
          <div className="mt-4 w-full">
            <div className="flex items-center justify-between text-[0.62rem] font-bold tracking-[0.16em] uppercase text-[#8fb4d8]">
              <span>Win odds</span>
              <span className={c.accent}>{pct}%</span>
            </div>
            <div className="mt-1.5 h-2 rounded-full bg-white/10 overflow-hidden">
              <div className={`h-full rounded-full ${c.bar}`} style={{ width: `${Math.max(pct, 3)}%` }} />
            </div>
          </div>
        </>
      ) : (
        <>
          <span className={`w-16 h-16 sm:w-[76px] sm:h-[76px] rounded-full border-[3px] border-dashed border-white/20 flex items-center justify-center`}>
            <Ticket className="w-6 h-6 text-white/30" />
          </span>
          <p className="mt-3 text-white/45 font-display font-bold text-[1rem]">Unclaimed</p>
          <p className="mt-1 text-[#8fb4d8]/60 text-[0.75rem]">Earn tickets to take this spot</p>
        </>
      )}
    </div>
  );
};

const CountdownRow = ({ countdown }) => (
  <div className="flex items-center justify-center gap-2.5">
    {[
      [countdown.d, "days"],
      [countdown.h, "hrs"],
      [countdown.m, "min"],
      [countdown.s, "sec"],
    ].map(([val, unit]) => (
      <div key={unit} className="glass-card-inner px-4 py-3 text-center min-w-[68px]">
        <p className="font-display font-bold text-white text-[1.5rem] leading-none">{pad(val)}</p>
        <p className="text-[#9fc5e8] text-[0.6rem] font-bold tracking-[0.2em] uppercase mt-1">{unit}</p>
      </div>
    ))}
  </div>
);

const EntryRow = ({ rank, entry, total }) => {
  const gold = rank <= 3;
  return (
    <div
      data-testid={`raffle-entry-row-${rank}`}
      className={`entry-row ${
        rank === 1
          ? "!border-yellow-300/35 !bg-yellow-300/8"
          : rank === 2
          ? "!border-slate-200/25 !bg-slate-200/5"
          : rank === 3
          ? "!border-orange-300/25 !bg-orange-300/5"
          : ""
      }`}
    >
      <span className="entry-rank">#{rank}</span>
      {entry.avatar_url && <img src={entry.avatar_url} alt="" className="w-6 h-6 rounded-full object-cover" />}
      <span className="text-white text-[0.88rem] font-bold truncate flex-1">{entry.username}</span>
      <span className="hidden sm:block text-[#8fb4d8] text-[0.72rem] font-bold w-14 text-right">
        {odds(entry.tickets, total)}%
      </span>
      <span className={`flex items-center gap-1 ${gold ? "text-[#ffd94a]" : "text-[#9fc5e8]"} text-[0.8rem] font-bold`}>
        <Ticket className="w-3.5 h-3.5" />
        {entry.tickets.toLocaleString()}
      </span>
    </div>
  );
};

/* -------- Active raffle: open layout, no card wrapper -------- */
const ActiveRaffle = ({ raffle }) => {
  const countdown = useCountdown(raffle.ends_at);
  const { user } = useAuth();
  const top3 = [0, 1, 2].map((i) => raffle.entries[i] || null);
  const myIndex = user ? raffle.entries.findIndex((e) => e.discord_id === user.discord_id) : -1;
  const mine = myIndex >= 0 ? raffle.entries[myIndex] : null;
  const shown = raffle.entries.slice(0, 10);
  const extra = raffle.entries.length - shown.length;

  return (
    <section className="mt-12" data-testid="active-raffle">
      <div className="text-center flex flex-col items-center">
        <div className="flex items-center gap-3 flex-wrap justify-center">
          <h2 className="heading-3d text-[1.9rem] sm:text-[2.5rem]">{raffle.title}</h2>
          <span className="px-3 py-1 rounded-full text-[0.65rem] font-bold tracking-[0.2em] uppercase border bg-emerald-400/15 border-emerald-300/30 text-emerald-200 animate-pulse">
            Live
          </span>
        </div>
        <p className="mt-2 text-[#9fc5e8] text-[0.85rem]">
          {raffle.winners_count} winner{raffle.winners_count > 1 ? "s" : ""} · {raffle.total_tickets.toLocaleString()} total tickets · {raffle.entries.length} participants
        </p>
        <div className="prize-banner mt-5">
          <Gift className="w-4 h-4 sm:w-5 sm:h-5" strokeWidth={2.6} />
          <span className="text-[0.62rem] sm:text-[0.7rem] tracking-[0.24em] uppercase opacity-80 font-bold">Prize</span>
          <span className="font-display font-bold text-[1.05rem] sm:text-[1.3rem] leading-none">{raffle.prize}</span>
        </div>
        {countdown && (
          <div className="mt-6 flex flex-col items-center gap-2">
            <span className="flex items-center gap-2 text-[#7fd0f5] text-[0.68rem] font-bold tracking-[0.26em] uppercase">
              <Clock className="w-3.5 h-3.5" />
              Draw in
            </span>
            <CountdownRow countdown={countdown} />
          </div>
        )}
        {user && (
          <div
            data-testid="raffle-my-entry"
            className="mt-8 w-full max-w-[560px] rounded-2xl border border-[#7fd0f5]/30 bg-[#7fd0f5]/10 backdrop-blur-md px-5 py-3.5 flex items-center gap-3.5"
          >
            <img src={user.avatar_url} alt="" className="w-10 h-10 rounded-full object-cover border-2 border-[#7fd0f5]/40" />
            <div className="flex-1 min-w-0 text-left">
              <p className="text-[#7fd0f5] text-[0.58rem] font-bold tracking-[0.24em] uppercase">Your entry</p>
              <p className="text-white font-display font-bold text-[0.98rem] truncate">
                {user.global_name || user.username}
              </p>
            </div>
            {mine ? (
              <div className="flex items-center gap-4 shrink-0">
                <span className="entry-rank">#{myIndex + 1}</span>
                <span className="hidden sm:block text-[#8fb4d8] text-[0.75rem] font-bold">
                  {odds(mine.tickets, raffle.total_tickets)}%
                </span>
                <span className="flex items-center gap-1 text-[#ffd94a] text-[0.85rem] font-bold">
                  <Ticket className="w-4 h-4" />
                  {mine.tickets.toLocaleString()}
                </span>
              </div>
            ) : (
              <span className="text-[#9fc5e8] text-[0.8rem] font-bold shrink-0">No tickets yet</span>
            )}
          </div>
        )}
      </div>

      {raffle.entries.length > 0 ? (
        <>
          <div className="mt-12 flex flex-col sm:flex-row items-stretch sm:items-end justify-center gap-8 sm:gap-5 lg:gap-7 max-w-[920px] mx-auto px-2">
            <TopEntryCard place={2} entry={top3[1]} total={raffle.total_tickets} />
            <TopEntryCard place={1} entry={top3[0]} total={raffle.total_tickets} />
            <TopEntryCard place={3} entry={top3[2]} total={raffle.total_tickets} />
          </div>

          <div className="mt-12 max-w-[760px] mx-auto">
            <h4 className="text-center text-[#cfe4fa] text-[0.7rem] font-bold tracking-[0.26em] uppercase">
              All participants
            </h4>
            <div className="mt-4 space-y-2">
              {shown.map((e, i) => (
                <EntryRow key={e.discord_id} rank={i + 1} entry={e} total={raffle.total_tickets} />
              ))}
            </div>
            {extra > 0 && (
              <p className="mt-3 text-center text-[#8fb4d8] text-[0.78rem] font-bold" data-testid="raffle-extra-count">
                +{extra} more participant{extra > 1 ? "s" : ""}
              </p>
            )}
          </div>
        </>
      ) : (
        <div className="mt-10 max-w-[560px] mx-auto rounded-3xl border border-white/12 bg-[rgba(6,32,66,0.45)] backdrop-blur-md px-8 py-10 text-center">
          <Ticket className="w-10 h-10 mx-auto text-[#7fd0f5]" strokeWidth={2} />
          <p className="mt-4 text-white font-display font-bold text-[1.15rem]">No tickets handed out yet</p>
          <p className="mt-2 text-[#9fc5e8] text-[0.9rem] leading-relaxed">
            Be the first on the podium — the king hands out tickets for activity. Stay active in the community to earn your spot!
          </p>
        </div>
      )}
    </section>
  );
};

/* -------- Past raffle: compact card, revealed by button -------- */
const PastRaffleCard = ({ raffle }) => {
  const winners = raffle.winners || [];
  return (
    <div className="glass-card p-5 sm:p-7">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-3 flex-wrap">
            <h4 className="font-display font-bold text-white text-[1.2rem]">{raffle.title}</h4>
            <span className="px-2.5 py-0.5 rounded-full text-[0.62rem] font-bold tracking-[0.16em] uppercase border bg-white/8 border-white/20 text-[#9fc5e8]">
              Ended
            </span>
          </div>
          <p className="mt-1 text-[#9fc5e8] text-[0.8rem]">
            <Gift className="w-3.5 h-3.5 inline mr-1 text-[#ffd94a]" />
            {raffle.prize} · {raffle.entries.length} participants · {raffle.total_tickets.toLocaleString()} tickets
          </p>
        </div>
        {raffle.drawn_at && (
          <p className="text-[#7fa8cc] text-[0.75rem]">Drawn {new Date(raffle.drawn_at).toLocaleDateString()}</p>
        )}
      </div>
      {winners.length > 0 && (
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2.5">
          {winners.map((w) => (
            <div key={w.discord_id} className="entry-row !border-yellow-300/30 !bg-yellow-300/5">
              <Trophy className="w-4 h-4 text-[#ffd94a] shrink-0" />
              <span className="entry-rank">#{w.place}</span>
              {w.avatar_url && <img src={w.avatar_url} alt="" className="w-6 h-6 rounded-full object-cover" />}
              <span className="text-white text-[0.88rem] font-bold truncate flex-1">{w.username}</span>
              <span className="text-[#ffd94a] text-[0.8rem] font-bold">{w.tickets.toLocaleString()} tickets</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const Raffles = () => {
  const [raffles, setRaffles] = useState(null);
  const [showPast, setShowPast] = useState(false);

  useEffect(() => {
    api
      .get("/raffles")
      .then((res) => setRaffles(res.data.raffles))
      .catch(() => setRaffles([]));
  }, []);

  const active = (raffles || []).filter((r) => r.status === "active");
  const past = (raffles || []).filter((r) => r.status !== "active");

  return (
    <main className="relative z-10 min-h-[100svh] pt-36 sm:pt-40 pb-24">
      <div className="max-w-[1200px] mx-auto px-5 sm:px-8">
        <Reveal>
          <div className="text-center flex flex-col items-center">
            <h1 className="heading-3d text-[2.4rem] sm:text-[3.4rem] lg:text-[4rem]">Raffle</h1>
            <div className="mt-8 w-full max-w-[720px] flex items-center gap-4" aria-hidden="true">
              <span className="h-px flex-1 bg-gradient-to-r from-transparent to-white/25" />
              <Ticket className="w-4 h-4 text-[#7fd0f5]" />
              <span className="h-px flex-1 bg-gradient-to-l from-transparent to-white/25" />
            </div>
          </div>
        </Reveal>

        {raffles === null && (
          <p className="mt-16 text-center text-[#bcd9f2]">Loading raffles...</p>
        )}

        {raffles !== null && active.length === 0 && (
          <Reveal delay={120}>
            <div className="mt-16 flex flex-col items-center text-center">
              <CrownIcon className="w-12 h-12 text-white/85 float-bob" />
              <h3 className="fx-liquid text-[2.4rem] sm:text-[3.2rem] mt-5">No live raffle</h3>
              <p className="mt-3 text-[#bcd9f2] text-[0.98rem]">The next golden ticket raffle is being prepared. Check back soon.</p>
            </div>
          </Reveal>
        )}

        {active.map((r, i) => (
          <Reveal key={r.id} delay={i * 120}>
            <ActiveRaffle raffle={r} />
          </Reveal>
        ))}

        {past.length > 0 && (
          <div className="mt-16 flex flex-col items-center">
            <button
              type="button"
              onClick={() => setShowPast((v) => !v)}
              className="btn-pill btn-ghost text-[0.75rem]"
            >
              {showPast ? <ChevronUp className="w-4 h-4" /> : <History className="w-4 h-4" />}
              {showPast ? "Hide previous raffles" : `Previous raffles (${past.length})`}
            </button>
            {showPast && (
              <div className="mt-8 w-full space-y-5">
                {past.map((r) => (
                  <PastRaffleCard key={r.id} raffle={r} />
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </main>
  );
};

export default Raffles;
